import React, { useState, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import { useUploadResumption } from './hooks/useUploadResumption.ts';
import { shouldShowMobileWarning } from './utils/deviceDetection.ts';
import HomeScreen from './screens/HomeScreen.tsx';
import UploadScreen from './screens/UploadScreen.tsx';
import ScanResultsScreen from './screens/ScanResultsScreen.tsx';
import CookiesScreen from './screens/CookiesScreen.tsx';
import PrivacyPolicyScreen from './screens/PrivacyPolicyScreen.tsx';
import TermsOfServiceScreen from './screens/TermsOfServiceScreen.tsx';
import LoginScreen from './screens/LoginScreen.tsx';
import SignupScreen from './screens/SignupScreen.tsx';
import AuthCallbackScreen from './screens/AuthCallbackScreen.tsx';
import DemoResultsScreen from './screens/DemoResultsScreen.tsx';
import UpgradeScreen from './screens/UpgradeScreen.tsx';
import UpgradeSuccessScreen from './screens/UpgradeSuccessScreen.tsx';
import ProfileScreen from './screens/ProfileScreen.tsx';
import YourScansScreen from './screens/YourScansScreen.tsx';
import BlogScreen from './screens/BlogScreen.tsx';
import BlogPostScreen from './screens/BlogPostScreen.tsx';
import BlogAdminScreen from './screens/admin/BlogAdminScreen.tsx';
import BlogPostAdminScreen from './screens/admin/BlogPostAdminScreen.tsx';
import SupabaseStatusBanner from './components/SupabaseStatusBanner.tsx';
import ResetPasswordScreen from './screens/ResetPasswordScreen.tsx';
import MobileWarningModal from './components/ui/MobileWarningModal.tsx';
import BoltBadge from './components/BoltBadge.tsx';
import { AdminRoute } from './components/AdminRoute.tsx';

const App: React.FC = () => {
  // Initialize upload resumption hook to handle post-login continuation
  const { isResuming, resumptionError, retryResumption, clearError, clearCacheAndError } = useUploadResumption();
  
  // Mobile warning state
  const [showMobileWarning, setShowMobileWarning] = useState(false);

  // Check for mobile warning on app load
  useEffect(() => {
    const hasSeenWarning = localStorage.getItem('safevibe-mobile-warning-dismissed');
    if (!hasSeenWarning && shouldShowMobileWarning()) {
      setShowMobileWarning(true);
    }
  }, []);

  const handleMobileWarningClose = () => {
    localStorage.setItem('safevibe-mobile-warning-dismissed', 'true');
    setShowMobileWarning(false);
  };

  const handleMobileWarningProceed = () => {
    localStorage.setItem('safevibe-mobile-warning-dismissed', 'true');
    setShowMobileWarning(false);
  };

  return (
    <>
      <SupabaseStatusBanner />
      {/* Bolt.new Hackathon Badge */}
      <BoltBadge />

      {/* Mobile Warning Modal */}
      <MobileWarningModal 
        isOpen={showMobileWarning}
        onClose={handleMobileWarningClose}
        onProceed={handleMobileWarningProceed}
      />

      {/* Show resumption status if active */}
      {isResuming && (
        <div className="fixed top-0 left-0 right-0 bg-blue-600 text-white p-3 z-50 text-center">
          <div className="flex items-center justify-center space-x-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            <span>🔄 Resuming your upload after login...</span>
          </div>
        </div>
      )}

      {/* Enhanced Resumption Error Banner with Cache Clear Option */}
      {resumptionError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <span className="mr-2">❌</span>
              <div>
                <p className="font-medium">Failed to resume your upload</p>
                <p className="text-sm">{resumptionError}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={retryResumption}
                className="bg-red-100 hover:bg-red-200 text-red-800 px-3 py-1 rounded text-sm font-medium"
              >
                Retry
              </button>
              <button
                onClick={clearCacheAndError}
                className="bg-orange-100 hover:bg-orange-200 text-orange-800 px-3 py-1 rounded text-sm font-medium"
              >
                Clear Cache
              </button>
              <button
                onClick={clearError}
                className="bg-gray-100 hover:bg-gray-200 text-gray-800 px-3 py-1 rounded text-sm font-medium"
              >
                Dismiss
              </button>
            </div>
          </div>
          <div className="mt-2 text-sm text-red-600">
            💡 Tip: If retry fails, try "Clear Cache" to remove any corrupted data.
          </div>
        </div>
      )}

      <Routes>
        <Route path="/" element={<HomeScreen />} />
        <Route path="/upload" element={<UploadScreen />} />
        <Route path="/scan-upload" element={<UploadScreen />} />
        <Route path="/results/demo" element={<DemoResultsScreen />} />
        <Route path="/results/:scanId" element={<ScanResultsScreen />} />
        <Route path="/demo" element={<DemoResultsScreen />} />
        <Route path="/demo-results" element={<DemoResultsScreen />} />
        <Route path="/cookies" element={<CookiesScreen />} />
        <Route path="/privacy" element={<PrivacyPolicyScreen />} />
        <Route path="/terms" element={<TermsOfServiceScreen />} />
        <Route path="/login" element={<LoginScreen />} />
        <Route path="/signup" element={<SignupScreen />} />
        <Route path="/reset-password" element={<ResetPasswordScreen />} />
        <Route path="/auth/callback" element={<AuthCallbackScreen />} />
        <Route path="/upgrade" element={<UpgradeScreen />} />
        <Route path="/upgrade-success" element={<UpgradeSuccessScreen />} />
        <Route path="/profile" element={<ProfileScreen />} />
        <Route path="/your-scans" element={<YourScansScreen />} />
        
        {/* Blog Routes */}
        <Route path="/blog" element={<BlogScreen />} />
        <Route path="/blog/:slug" element={<BlogPostScreen />} />
        
        {/* Admin Routes - Protected with AdminRoute component */}
        <Route path="/admin/blog" element={
          <AdminRoute>
            <BlogAdminScreen />
          </AdminRoute>
        } />
        <Route path="/admin/blog/:slug" element={
          <AdminRoute>
            <BlogPostAdminScreen />
          </AdminRoute>
        } />
        
        {/* Add more routes as we implement them */}
      </Routes>
    </>
  );
};

export default App; 